import { signal } from '@preact/signals';

// Every sound is synthesized with Web Audio: a pin, a stamp, paper. No audio files to ship.

const STORAGE_KEY = 'gazette.muted';

export const muted = signal(localStorage.getItem(STORAGE_KEY) === '1');

export function toggleMuted(): void {
  muted.value = !muted.value;
  localStorage.setItem(STORAGE_KEY, muted.value ? '1' : '0');
}

let context: AudioContext | null = null;
let noiseBuffer: AudioBuffer | null = null;
let unlocked = false;

function audio(): AudioContext | null {
  if (muted.value || !unlocked) return null;
  context ??= new AudioContext();
  if (context.state === 'suspended') void context.resume();
  return context;
}

// Browsers only start audio after a gesture; until the first tap or key, the game is silent.
for (const event of ['pointerdown', 'keydown'] as const) {
  window.addEventListener(
    event,
    () => {
      unlocked = true;
      void audio();
    },
    { once: true },
  );
}

function noise(ctx: AudioContext): AudioBuffer {
  if (!noiseBuffer) {
    noiseBuffer = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  }
  return noiseBuffer;
}

function envelope(ctx: AudioContext, start: number, peak: number, attack: number, release: number): GainNode {
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(peak, start + attack);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + attack + release);
  gain.connect(ctx.destination);
  return gain;
}

function burst(ctx: AudioContext, at: number, filter: BiquadFilterType, frequency: number, peak: number, release: number, q = 1): void {
  const source = ctx.createBufferSource();
  source.buffer = noise(ctx);
  const band = ctx.createBiquadFilter();
  band.type = filter;
  band.frequency.value = frequency;
  band.Q.value = q;
  source.connect(band).connect(envelope(ctx, at, peak, 0.004, release));
  source.start(at, Math.random() * 0.5, release + 0.05);
}

function tone(ctx: AudioContext, at: number, type: OscillatorType, from: number, to: number, peak: number, release: number): void {
  const osc = ctx.createOscillator();
  osc.type = type;
  osc.frequency.setValueAtTime(from, at);
  osc.frequency.exponentialRampToValueAtTime(to, at + release);
  osc.connect(envelope(ctx, at, peak, 0.006, release));
  osc.start(at);
  osc.stop(at + release + 0.05);
}

export const sfx = {
  /** A pin pushed into cork. */
  pin(delay = 0) {
    const ctx = audio();
    if (!ctx) return;
    const at = ctx.currentTime + delay;
    burst(ctx, at, 'highpass', 2600, 0.25, 0.03);
    tone(ctx, at, 'triangle', 1800, 1200, 0.05, 0.05);
  },
  /** The rubber stamp hitting the table. */
  stamp() {
    const ctx = audio();
    if (!ctx) return;
    const at = ctx.currentTime;
    tone(ctx, at, 'sine', 110, 38, 0.7, 0.22);
    burst(ctx, at, 'lowpass', 500, 0.6, 0.12);
    burst(ctx, at + 0.01, 'bandpass', 1600, 0.12, 0.05, 2);
  },
  /** Paper through the air. */
  swoosh(duration = 0.45) {
    const ctx = audio();
    if (!ctx) return;
    const at = ctx.currentTime;
    const source = ctx.createBufferSource();
    source.buffer = noise(ctx);
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.Q.value = 1.2;
    band.frequency.setValueAtTime(300, at);
    band.frequency.exponentialRampToValueAtTime(2400, at + duration);
    source.connect(band).connect(envelope(ctx, at, 0.22, duration * 0.6, duration * 0.5));
    source.start(at, 0, duration + 0.1);
  },
  /** The press running off the edition. */
  press() {
    const ctx = audio();
    if (!ctx) return;
    for (let i = 0; i < 6; i++) burst(ctx, ctx.currentTime + i * 0.14, 'bandpass', 900 + (i % 2) * 400, 0.3, 0.05, 3);
  },
  /** A thread pulled taut. */
  pluck(delay = 0) {
    const ctx = audio();
    if (!ctx) return;
    tone(ctx, ctx.currentTime + delay, 'triangle', 320, 520, 0.12, 0.14);
  },
  /** Suspense before the author is named. */
  drumroll(duration = 1.1) {
    const ctx = audio();
    if (!ctx) return;
    const at = ctx.currentTime;
    for (let i = 0; i < duration * 22; i++) {
      burst(ctx, at + i / 22, 'bandpass', 220, 0.05 + (i / (duration * 22)) * 0.2, 0.04, 1.5);
    }
  },
  /** Last seconds of a timer. */
  tick() {
    const ctx = audio();
    if (!ctx) return;
    tone(ctx, ctx.currentTime, 'square', 1400, 1300, 0.03, 0.03);
  },
};
